import React, { useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from '@xstate/react';
import { Accordion } from '@chargify/custom-ui-components';

import ShowMore from '../../components/ShowMore';
import { SubscriptionContainer } from '../SubscriptionContainer';
import { ItemsContainer } from './SubscriptionsContainer.styles';

import {
  subscriptionsSelectors,
  productCatalogSelectors,
  DEFAULT_VISIBLE_SUBSCRIPTIONS,
} from '../../machines';
import { GlobalStateContext, ConfigContext } from '../../contexts';
import { sortSubscriptions } from '../../utils';

export type SubscriptionsContainerProps = {
  /**
   * Product catalog identifier
   */
  catalogId: number;
  /**
   * Product catalog name
   */
  catalogName: string;
  /**
   * Renders subscriptions in expandable accordions
   */
  useExpandableLayout?: boolean;
};

const SubscriptionsContainer = ({
  catalogId,
  catalogName,
  useExpandableLayout = false,
}: SubscriptionsContainerProps) => {
  const { subscriptionsService, productCatalogService } =
    useContext(GlobalStateContext);
  const { subscription } = useContext(ConfigContext);
  const navigate = useNavigate();

  const expandedCatalogItems = useSelector(
    productCatalogService,
    productCatalogSelectors.getExpandedCatalogItems
  );

  const subscriptionsWithVisibleDetails = useSelector(
    subscriptionsService,
    subscriptionsSelectors.getSubscriptionsWithVisibleDetails
  );

  const totalSubscriptions = useSelector(
    subscriptionsService,
    subscriptionsSelectors.getSubscriptionsCount
  );

  const subscriptions = useSelector(
    subscriptionsService,
    useCallback(
      subscriptionsSelectors.getSubscriptionsByCategoryId(catalogId),
      []
    )
  );

  const { isListExpanded, isPaginated } = useSelector(
    subscriptionsService,
    useCallback(subscriptionsSelectors.getCatalogById(catalogId), [])
  );

  const subscriptionLayout =
    totalSubscriptions > 1 ? 'expandable-card' : 'all-details';

  const onShowSubscriptionDetails = useCallback(
    (subscriptionId: number) => {
      if (totalSubscriptions > 1) {
        if (subscription.details === 'page')
          navigate(`subscription/${catalogId}/${subscriptionId}`);
        else {
          subscriptionsService.send('TOGGLE_SUBSCRIPTION_DETAILS_VISIBILITY', {
            subscriptionId: subscriptionId,
          });
        }
      }
    },
    [subscription.details]
  );

  const items = subscriptions
    .sort((a, b) => sortSubscriptions(a.subscription, b.subscription))
    .filter(
      (item, idx) =>
        (isListExpanded || idx < DEFAULT_VISIBLE_SUBSCRIPTIONS) && item
    )
    .map(({ subscription }) => (
      <SubscriptionContainer
        key={subscription.id}
        id={subscription.id}
        layout={subscriptionLayout}
        product={subscription.product}
        status={subscription.status}
        productFamilyId={subscription.productFamilyId}
        components={subscription.components}
        currency={subscription.currency}
        detailsVisible={
          subscriptionLayout === 'all-details' ||
          subscriptionsWithVisibleDetails.includes(subscription.id)
        }
        onShowDetails={(subscriptionId) =>
          onShowSubscriptionDetails(subscriptionId)
        }
      />
    ));

  const renderShowMoreBtn = isPaginated && (
    <ShowMore
      isExpanded={isListExpanded}
      items={subscriptions.length}
      onClick={() =>
        subscriptionsService.send('SET_LIST_EXPANDED', {
          catalogId,
          status: !isListExpanded,
        })
      }
    />
  );

  return (
    <>
      {useExpandableLayout ? (
        <Accordion
          header={catalogName}
          isExpanded={expandedCatalogItems.includes(catalogId)}
          onClick={(isExpanded) => {
            productCatalogService.send('SET_CATALOG_EXPAND', {
              catalogId,
              isExpanded,
            });
          }}
        >
          <ItemsContainer paddingBottom={5}>
            {items}
            {renderShowMoreBtn}
          </ItemsContainer>
        </Accordion>
      ) : (
        <>
          {items}
          {renderShowMoreBtn}
        </>
      )}
    </>
  );
};

export default SubscriptionsContainer;
